
import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';

// EmailJS credentials
const EMAILJS_PUBLIC_KEY = 'NWv6SPH-0SYlMXyma';
const EMAILJS_SERVICE_ID = 'service_wuhk067';
const EMAILJS_TEMPLATE_ID = 'template_r3o138o';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize EmailJS once on component mount
  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          title: 'Kontakt sa Synorio sajta',
        }
      );

      if (result.status === 200) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        if ((window as any).gtag) {
          (window as any).gtag('event', 'contact_form_submit', {
            event_category: 'Contact',
            event_label: formData.email,
          });
        }
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Došlo je do greške prilikom slanja poruke. Molimo pokušajte ponovo.');
      console.error('Error sending email:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto space-y-6 p-0 sm:p-2 md:p-4 lg:p-6 flex flex-col items-center">
      <div className="w-full space-y-2">
        <label htmlFor="name" className="block text-sm font-medium text-gray-300">
          Ime
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-synorio-purple/50 text-white placeholder-gray-400"
          placeholder="Vaše ime"
        />
      </div>

      <div className="w-full space-y-2">
        <label htmlFor="email" className="block text-sm font-medium text-gray-300">
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-synorio-purple/50 text-white placeholder-gray-400"
          placeholder="Vaš email"
        />
      </div>

      <div className="w-full space-y-2">
        <label htmlFor="message" className="block text-sm font-medium text-gray-300">
          Poruka
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-synorio-purple/50 text-white placeholder-gray-400 resize-none"
          placeholder="Vaša poruka..."
        />
      </div>

      {status === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-400 text-sm w-full text-center"
        >
          {errorMessage}
        </motion.div>
      )}

      {status === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-green-400 text-sm w-full text-center"
        >
          Poruka je uspešno poslata! Odgovorićemo vam u najkraćem mogućem roku.
        </motion.div>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full sm:w-1/2 md:w-1/3 flex items-center justify-center gap-2 px-6 py-3 bg-synorio-purple text-white font-semibold rounded-xl hover:bg-synorio-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
      >
        {status === 'sending' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Slanje...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Pošalji poruku
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
